import React, { useState, useEffect } from 'react';
import { Strategy, Symbol, ExecutionTimingSettings, RiskParameters, AggregatedSymbol } from './types';
import StewieQuantDashboardIntegration from './StewieQuantDashboardIntegration';
import { useToast } from "@/hooks/use-toast";
import { useRealMarketData } from './hooks/useRealMarketData';

interface StewieQuantAlgorithmProps {
  isExpanded?: boolean;
  setIsExpanded?: (expanded: boolean) => void;
  onCreateStrategy?: (strategy: Strategy) => void;
  activeStrategy?: Strategy | null;
  onFetchMarketData?: (symbols: AggregatedSymbol[]) => void;
}

const StewieQuantAlgorithm: React.FC<StewieQuantAlgorithmProps> = ({
  isExpanded = false,
  setIsExpanded = () => {},
  onCreateStrategy,
  activeStrategy = null,
  onFetchMarketData
}) => {
  const { toast } = useToast();
  const [isInitialized, setIsInitialized] = useState(false);
  const [showStrategyDialog, setShowStrategyDialog] = useState(false);
  const [pendingStrategyData, setPendingStrategyData] = useState<any>(null);
  
  const { 
    isLoading: isLoadingMarketData, 
    fetchMarketData,
    marketData 
  } = useRealMarketData();

  useEffect(() => {
    const initializeAlgorithm = async () => {
      try {
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if (activeStrategy) {
          const symbols = getSymbolsFromStrategy(activeStrategy);
          if (symbols.length > 0) {
            try {
              if (onFetchMarketData) {
                onFetchMarketData(symbols);
              } else {
                await fetchMarketData(symbols);
              }
            } catch (error) {
              console.error('Failed to fetch market data:', error);
            }
          }
        }
        
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize StewieQuant:', error);
        toast({
          title: "Initialization Error",
          description: "Failed to initialize the StewieQuant. Please try again.",
          variant: "destructive"
        });
      }
    };

    initializeAlgorithm();
  }, [activeStrategy, toast, onFetchMarketData, fetchMarketData]);

  const getSymbolsFromStrategy = (strategy: Strategy): AggregatedSymbol[] => {
    if (strategy.params?.aggregatedSymbols && strategy.params.aggregatedSymbols.length > 0) {
      return strategy.params.aggregatedSymbols;
    }
    
    // First, try to extract symbols from baskets
    if (strategy.baskets && Array.isArray(strategy.baskets) && strategy.baskets.length > 0) {
      const basketSymbols: AggregatedSymbol[] = [];
      strategy.baskets.forEach(basket => {
        if (basket.symbols && Array.isArray(basket.symbols)) {
          basket.symbols.forEach(symbol => {
            basketSymbols.push({
              id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
              symbol: symbol.ticker || symbol.symbol || '',
              profitTarget: symbol.profitTarget || basket.profitTarget || '',
              stopLoss: symbol.stopLoss || basket.stopLoss || '',
              direction: symbol.direction as 'long' | 'short' | 'auto'
            });
          });
        }
      });
      if (basketSymbols.length > 0) {
        return basketSymbols;
      }
    }
    
    // Fallback to regular symbols field
    if (typeof strategy.symbols === 'string') {
      try {
        const symbolString = strategy.symbols || '';
        // Check if the string is valid before splitting
        const symbolList = typeof symbolString === 'string' ? 
          symbolString.split(',').map(s => s.trim()) : 
          [];
        
        return symbolList.map(symbol => ({
          id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
          symbol,
          profitTarget: '',
          stopLoss: ''
        }));
      } catch (error) {
        console.error("Error parsing symbols string:", error);
        return [];
      }
    }
    
    if (Array.isArray(strategy.symbols)) {
      return strategy.symbols.map(symb => ({
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        symbol: symb.ticker || symb.symbol || '', // Handle both ticker and symbol
        profitTarget: '',
        stopLoss: '',
        direction: symb.direction as 'long' | 'short' | 'auto'
      }));
    }
    
    return [];
  };

  const defaultExecutionTiming: ExecutionTimingSettings = {
    signalFrequency: 'daily_close',
    monitorFrequency: 'matches_signal',
    executeRthOnly: true,
    allowPrePostMarket: false,
  };

  const defaultRiskParameters: RiskParameters = {
    maxPositionSizePct: 0.05,
    maxStrategyExposurePct: 100,
    isVolWeightedRisk: true,
    allowDynamicExits: true,
    stopLossType: 'percentage',
    stopLossValue: '2',
    profitTargetType: 'percentage',
    profitTargetValue: '5',
    atrPeriod: 14,
    volatilityPeriod: 20,
    correlationThreshold: 0.7,
    zscoreThreshold: 2.0
  };

  const createBuiltinStrategies = () => {
    const strategies: Strategy[] = [
      {
        id: 'sq_momentum',
        name: 'StewieQuant Momentum Alpha',
        description: "Multi-factor momentum strategy",
        engine: 'stewie_quant',
        riskLevel: 'high', // Changed from 'aggressive' to 'high'
        symbols: [
          { ticker: 'AAPL', symbol: 'AAPL', direction: 'long', weight: 0.2 }, // Added weight
          { ticker: 'MSFT', symbol: 'MSFT', direction: 'long', weight: 0.2 },
          { ticker: 'AMZN', symbol: 'AMZN', direction: 'long', weight: 0.2 },
          { ticker: 'TSLA', symbol: 'TSLA', direction: 'long', weight: 0.2 },
          { ticker: 'NVDA', symbol: 'NVDA', direction: 'long', weight: 0.2 }
        ],
        executionTiming: defaultExecutionTiming,
        riskParameters: {
          ...defaultRiskParameters,
          maxPositionSizePct: 0.1, // 10% per position
          allowDynamicExits: false
        },
        models: [
          {
            type: 'stewie_quant',
            weight: 1,
            decisionControl: 'signal_generation',
            stewieQuantSettings: {
              optimizerLevel: 3,
              adaptiveThreshold: 0.75,
              correlationThreshold: 0.7,
              zscoreThreshold: 2.0,
              reversionHalfLife: 15
            }
          }
        ],
        backtestSettings: {
          mode: 'backtest',
          startDate: '2023-01-01',
          endDate: '2023-12-31',
          provider: 'yahoo',
          environment: 'all',
          marketEnvironment: 'all',
          initialCapital: '100000',
          includeCommission: true,
          commissionRate: '0.0035',
          includeSlippage: true,
          slippageModel: 'fixed',
          slippageValue: '0.001',
          executionDelay: '100',
          useRealTimeData: false,
          dataUpdateInterval: '60'
        },
        exitConditions: [],
        baskets: []
      }
    ];
    
    return strategies;
  };

  return (
    <div className={`transition-all duration-300 ease-in-out ${
      isExpanded ? 'col-span-3' : 'col-span-3 lg:col-span-1'
    }`}
    >
      <StewieQuantDashboardIntegration
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        onCreateStrategy={onCreateStrategy}
        activeStrategy={activeStrategy}
        onFetchMarketData={onFetchMarketData}
      />
    </div>
  );
};

export default StewieQuantAlgorithm;
